// Minimal base64url decoder that works in browser and Node/SSR
function decodeBase64Url(input) {
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed for proper base64 decoding
    const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);

    if (typeof atob === 'function') {
        return atob(paddedBase64);
    }
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(paddedBase64, 'base64').toString('utf-8');
    }
    throw new Error('No base64 decoder available');
}

/**
 * Parse JWT token and return payload
 * @param {string} token - JWT token string
 * @returns {object|null} - Parsed JWT payload or null if invalid
 */
export function parseJWT(token) {
    if (!token || typeof token !== 'string') {
        return null;
    }

    try {
        // JWT has 3 parts separated by dots: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const payloadPart = parts[1];
        const decodedPayload = decodeBase64Url(payloadPart);
        const parsed = JSON.parse(decodedPayload);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
        return null;
    }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} - True if token is expired
 */
export function isJWTExpired(token) {
    const payload = parseJWT(token);
    if (!payload || !payload.exp) {
        return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now();
}

/**
 * Get expiration time from JWT token
 * @param {string} token - JWT token string
 * @returns {number|null} - Expiration timestamp in milliseconds or null
 */
export function getJWTExpiration(token) {
    const payload = parseJWT(token);
    if (!payload || !payload.exp) {
        return null;
    }

    return payload.exp * 1000;
}

/**
 * Check if JWT access token represents an active delegation session.
 * A token is considered a delegation token when it has exchange_reason === 'delegation'
 * AND a non-empty exchange_actor_user_id claim.
 * @param {string} token - JWT access token string
 * @returns {boolean}
 */
export function getIsDelegationToken(token) {
    const payload = parseJWT(token);
    if (!payload) {
        return false;
    }

    const isDelegationReason = payload.exchange_reason === 'delegation';
    const hasActorUserId = typeof payload.exchange_actor_user_id === 'string'
        && payload.exchange_actor_user_id.length > 0;

    return isDelegationReason && hasActorUserId;
}

/**
 * Check if JWT access token represents an active group-member session.
 * A token is considered a member token when it has exchange_reason === 'member'
 * AND a non-empty exchange_actor_user_id claim.
 * @param {string} token - JWT access token string
 * @returns {boolean}
 */
export function getIsMemberToken(token) {
    const payload = parseJWT(token);
    if (!payload) {
        return false;
    }

    const isMemberReason = payload.exchange_reason === 'member';
    const hasActorUserId = typeof payload.exchange_actor_user_id === 'string'
        && payload.exchange_actor_user_id.length > 0;

    return isMemberReason && hasActorUserId;
}

/**
 * Check if JWT access token represents acting on behalf of another user
 * (either delegation or group-member exchange).
 * @param {string} token - JWT access token string
 * @returns {boolean}
 */
export function getIsActorToken(token) {
    return getIsDelegationToken(token) || getIsMemberToken(token);
}

/**
 * Extract a transaction-scope restriction from a JWT access token.
 * The "default" sentinel and a missing claim both mean "no restriction".
 * @param {string} token - JWT access token string
 * @returns {string|null} - Concrete transaction id, or null when unrestricted
 */
export function getScopedTransactionId(token) {
    const id = parseJWT(token)?.transaction_id;
    if (!id || id === 'default') return null;
    return id;
}

/**
 * Extract the active account id from a JWT access token's session_account_id claim.
 * The "default" sentinel, missing, or empty claim all mean "no concrete account in token".
 * @param {string} token - JWT access token string
 * @returns {string|null} - Concrete account id, or null when not set
 */
export function getSessionAccountId(token) {
    const id = parseJWT(token)?.session_account_id;
    if (!id || id === 'default') return null;
    return id;
}
